import { useState } from 'react';
import CustomWatchlistReportService, { CustomReportAlumniRow } from './CustomWatchlistReportService';
import { Overlay, useSave } from './ReportOverlay';
import { MANUAL_STAGES, STAGE_DESCRIPTIONS_CUSTOM, STAGE_LABELS_CUSTOM, canSelectStage } from './customStage';

const OPTIONS = MANUAL_STAGES.map((code) => ({
    code,
    label: STAGE_LABELS_CUSTOM[code],
    description: STAGE_DESCRIPTIONS_CUSTOM[code],
}));

const UpdateStageModal = ({
    row,
    watchlistId,
    instituteId,
    filterInstituteId,
    onClose,
    onDone,
}: {
    row: CustomReportAlumniRow;
    watchlistId: string | number;
    instituteId: string | number;
    filterInstituteId?: string | null;
    onClose: () => void;
    onDone: () => void;
}) => {
    const effective = row.stage || 'identified';
    const persisted = row.persisted_stage || null;
    const alreadyContributing = effective === 'contribution';
    const alreadyDenied = effective === 'denied';
    const [selected, setSelected] = useState<string>('');
    const [remarks, setRemarks] = useState('');

    const canSave = canSelectStage({ effective, persisted, target: selected });
    const { saving, error, run } = useSave(
        () => CustomWatchlistReportService.updateStage(
            watchlistId,
            row.student_id,
            { stage: selected, remarks: remarks.trim() || null },
            { institute_id: instituteId, filter_institute_id: filterInstituteId },
        ),
        onDone,
    );

    return (
        <Overlay
            title="Update Stage"
            onClose={onClose}
            footer={
                <>
                    <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-primary px-4 rounded-3" onClick={run} disabled={saving || !canSave}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </>
            }>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            <p className="font-13 text-muted mb-3">
                Manually update the activation stage for <span className="font-600 text-dark-blue">{row.name ?? 'this alumni'}</span>
            </p>
            {alreadyDenied && (
                <div className="alert alert-secondary font-12">
                    This alumnus is marked Denied. Stage cannot be moved. Remove them from the watchlist if they should leave this list.
                </div>
            )}
            {alreadyContributing && !alreadyDenied && (
                <div className="alert alert-secondary font-12">
                    This alumni is already at Contributing. Stage cannot be moved backward.
                </div>
            )}
            <div className="d-flex flex-column gap-2 mb-3">
                {OPTIONS.map((opt) => {
                    const disabled = opt.code === 'denied'
                        ? alreadyDenied
                        : alreadyDenied || !canSelectStage({ effective, persisted, target: opt.code });
                    const isCurrent = (MANUAL_STAGES as readonly string[]).includes(persisted || '') && persisted === opt.code;
                    return (
                        <label
                            key={opt.code}
                            className={`border rounded-3 p-3 mb-0 ${disabled ? 'opacity-50' : 'cursor-pointer'} ${selected === opt.code ? 'border-primary' : ''}`}>
                            <div className="d-flex align-items-start gap-2">
                                <input
                                    type="radio"
                                    className="mt-1"
                                    name="custom-stage"
                                    value={opt.code}
                                    checked={selected === opt.code}
                                    disabled={disabled}
                                    onChange={() => setSelected(opt.code)}
                                />
                                <div>
                                    <div className="font-14 font-600 text-dark-blue">
                                        {opt.label}
                                        {isCurrent && <span className="badge bg-secondary bg-opacity-10 text-secondary font-10 ms-2">Current</span>}
                                    </div>
                                    <div className="font-12 text-muted">{opt.description}</div>
                                </div>
                            </div>
                        </label>
                    );
                })}
            </div>
            <label className="form-label text-muted font-12 font-600">Remarks (optional)</label>
            <textarea
                className="form-control rounded-3 h-auto font-14"
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional context…"
                maxLength={2000}
            />
        </Overlay>
    );
};

export default UpdateStageModal;
